import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Spinner } from "@/components/kit";
import { useAdminQuery } from "@/hooks/queries";
import { AdminHeader, StatCard } from "@/pages/admin/AdminLayout";

const sum = (a = []) => a.reduce((x, y) => x + y, 0);

function Chart({ title, data, k, color, bar }) {
  const C = bar ? BarChart : LineChart;
  return (
    <div className="t-card p-5">
      <p className="text-[15px] font-bold mb-3">{title}</p>
      <div className="h-56">
        <ResponsiveContainer>
          <C data={data}>
            <CartesianGrid stroke="#F1F5F9" vertical={false} />
            <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(d) => d.slice(5)} />
            <YAxis tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} width={36} />
            <Tooltip />
            {bar ? <Bar dataKey={k} fill={color} radius={[6, 6, 0, 0]} /> : <Line type="monotone" dataKey={k} stroke={color} strokeWidth={2.5} dot={false} />}
          </C>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function AdminAnalytics() {
  const { data, isLoading, error } = useAdminQuery("/admin/analytics");
  if (isLoading) return <Spinner />;
  if (error) return <p className="text-[#DC2626]">{error.message}</p>;
  const series = (data.days ?? []).map((day, i) => ({ day, newUsers: data.newUsers?.[i] ?? 0, scans: data.scans?.[i] ?? 0, revenue: data.revenue?.[i] ?? 0 }));
  const dist = data.categoryDistribution ?? [];
  const max = Math.max(1, ...dist.map((d) => d.count));
  return (
    <>
      <AdminHeader title="Analytics" sub={`Last ${series.length} days`} />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <StatCard label="New users" value={sum(data.newUsers)} testid="analytics-new-users" />
        <StatCard label="Scans" value={sum(data.scans)} accent="#2563EB" testid="analytics-scans" />
        <StatCard label="Revenue" value={`₹${sum(data.revenue).toLocaleString("en-IN")}`} accent="#059669" testid="analytics-revenue" />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <Chart title="New users" data={series} k="newUsers" color="#0000EC" />
        <Chart title="Scans" data={series} k="scans" color="#2563EB" bar />
        <Chart title="Revenue (₹)" data={series} k="revenue" color="#059669" />
        <div className="t-card p-5" data-testid="analytics-categories">
          <p className="text-[15px] font-bold mb-3">Contacts by category</p>
          <div className="space-y-2.5">
            {dist.map((d) => (
              <div key={d.name} className="text-[13px]">
                <div className="flex justify-between mb-1"><span className="font-medium">{d.name}</span><span className="text-[#64748B]">{d.count}</span></div>
                <div className="h-2 rounded-full bg-[#F1F5F9]"><div className="h-full rounded-full bg-[#7C3AED]" style={{ width: `${(d.count / max) * 100}%` }} /></div>
              </div>
            ))}
            {!dist.length && <p className="text-[14px] text-[#64748B]">No data yet.</p>}
          </div>
        </div>
      </div>
    </>
  );
}
